import { useLocation } from 'react-router-dom';
import { useViewTransitionNav } from './viewTransition';

export interface BackNavState {
  backPath?: string;
  backLabel?: string;
  [key: string]: unknown;
}

/**
 * Shared "back" behaviour for detail pages. When the page was reached by navigating within the app,
 * "back" pops browser history (`navigate(-1)`) so the user returns along the exact path they took —
 * e.g. member → product → sprint walks back to the product, then to the member, instead of jumping to a
 * hardcoded parent.
 *
 * `fallbackPath`/`fallbackLabel` are used only when there is no in-app history to pop: a deep link, a
 * refresh landing on the first entry, or a fresh tab. Callers navigating forward can still set
 * `location.state.backLabel` so the chevron reads "Back to <the page you came from>".
 */
export const useBackNavigation = (
  fallbackPath: string,
  fallbackLabel: string,
): { path: string; label: string; goBack: () => void } => {
  const location = useLocation();
  const transitionNavigate = useViewTransitionNav();
  const state = (location.state ?? null) as BackNavState | null;

  const label = state?.backLabel ?? fallbackLabel;
  // For callers that render the control as a real link: the href it points at, so middle-click and
  // "open in new tab" still work while a plain click pops history.
  const path = state?.backPath ?? fallbackPath;

  const goBack = (): void => {
    // React Router records the current entry's position in the session history. idx > 0 guarantees the
    // previous entry is an in-app route, so popping history keeps us inside the app and follows the
    // real navigation path. idx 0 (or missing) means there's nothing in-app to go back to.
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) {
      transitionNavigate(-1, 'back');
      return;
    }
    // Replace rather than push: the detail page is the first entry, so pushing the fallback would leave
    // it on the stack and the browser's own Back control would land the user right back on it.
    transitionNavigate(state?.backPath ?? fallbackPath, 'back', { replace: true });
  };

  return { path, label, goBack };
};
